import type { AnnotationAction } from './types';

export interface AnnotationTextSegment {
  id: string;
  order_index: number;
  text: string;
  start_s?: number;
  end_s?: number;
}

export interface SelectionPoint {
  segment_id: string;
  offset: number;
}

export interface AnchoredSelection {
  start_segment_id: string;
  start_offset: number;
  end_segment_id: string;
  end_offset: number;
}

export interface SelectionSlice {
  segment_id: string;
  start_offset: number;
  end_offset: number;
  text: string;
}

export interface AnchorAnnotation extends AnchoredSelection {
  id: string;
  action: AnnotationAction;
  reviewer_id: string;
  note?: string | null;
}

export interface AnnotationFilter {
  reviewer_id?: string | null;
  actions?: AnnotationAction[];
  query?: string;
}

export interface LayeredRange {
  id: string;
  start: number;
  end: number;
  layer: number;
}

export type AnnotationSegmentRange = AnchorAnnotation & {
  start: number;
  end: number;
  layer: number;
};

interface OrderedSelectionContext {
  segments: AnnotationTextSegment[];
  indexById: Map<string, number>;
}

function orderedSegments(segments: readonly AnnotationTextSegment[]): AnnotationTextSegment[] {
  return [...segments].sort((left, right) => left.order_index - right.order_index);
}

function selectionContext(segments: readonly AnnotationTextSegment[]): OrderedSelectionContext {
  const ordered = orderedSegments(segments);
  return {
    segments: ordered,
    indexById: new Map(ordered.map((segment, index) => [segment.id, index])),
  };
}

function checkedPoint(
  context: OrderedSelectionContext,
  point: SelectionPoint,
): { point: SelectionPoint; index: number } {
  const index = context.indexById.get(point.segment_id);
  if (index === undefined) throw new RangeError(`Unknown transcript segment: ${point.segment_id}`);
  const length = context.segments[index]!.text.length;
  if (!Number.isInteger(point.offset) || point.offset < 0 || point.offset > length) {
    throw new RangeError(`Selection offset must be from 0 to ${length}.`);
  }
  return { point, index };
}

function normaliseSelectionInContext(
  context: OrderedSelectionContext,
  anchor: SelectionPoint,
  focus: SelectionPoint,
): AnchoredSelection | null {
  const left = checkedPoint(context, anchor);
  const right = checkedPoint(context, focus);
  const reversed =
    left.index > right.index ||
    (left.index === right.index && left.point.offset > right.point.offset);
  const start = reversed ? right : left;
  const end = reversed ? left : right;
  if (start.point.segment_id === end.point.segment_id && start.point.offset === end.point.offset) return null;
  if (start.point.segment_id !== end.point.segment_id) {
    const includesCharacters = start.point.offset < context.segments[start.index]!.text.length
      || end.point.offset > 0
      || context.segments.slice(start.index + 1, end.index).some((segment) => segment.text.length > 0);
    if (!includesCharacters) return null;
  }
  return {
    start_segment_id: start.point.segment_id,
    start_offset: start.point.offset,
    end_segment_id: end.point.segment_id,
    end_offset: end.point.offset,
  };
}

function selectionSlicesInContext(
  context: OrderedSelectionContext,
  selection: AnchoredSelection,
): SelectionSlice[] {
  const normalised = normaliseSelectionInContext(
    context,
    { segment_id: selection.start_segment_id, offset: selection.start_offset },
    { segment_id: selection.end_segment_id, offset: selection.end_offset },
  );
  if (!normalised) return [];
  const startIndex = context.indexById.get(normalised.start_segment_id)!;
  const endIndex = context.indexById.get(normalised.end_segment_id)!;
  const slices: SelectionSlice[] = [];
  for (let index = startIndex; index <= endIndex; index += 1) {
    const segment = context.segments[index]!;
    const startOffset = index === startIndex ? normalised.start_offset : 0;
    const endOffset = index === endIndex ? normalised.end_offset : segment.text.length;
    if (endOffset <= startOffset) continue;
    slices.push({
      segment_id: segment.id,
      start_offset: startOffset,
      end_offset: endOffset,
      text: segment.text.slice(startOffset, endOffset),
    });
  }
  return slices;
}

/** Normalises anchor/focus points into a forward, end-exclusive transcript range. */
export function normaliseSelection(
  segments: readonly AnnotationTextSegment[],
  anchor: SelectionPoint,
  focus: SelectionPoint,
): AnchoredSelection | null {
  return normaliseSelectionInContext(selectionContext(segments), anchor, focus);
}

/** Returns the exact selected characters in every covered segment. */
export function selectionSlices(
  segments: readonly AnnotationTextSegment[],
  selection: AnchoredSelection,
): SelectionSlice[] {
  return selectionSlicesInContext(selectionContext(segments), selection);
}

export function selectionText(
  segments: readonly AnnotationTextSegment[],
  selection: AnchoredSelection,
): string {
  return selectionSlices(segments, selection)
    .map((slice) => slice.text)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Converts a segment-local character offset to a joined-transcript offset. */
export function toGlobalOffset(
  segments: readonly AnnotationTextSegment[],
  point: SelectionPoint,
  separatorLength = 1,
): number {
  if (!Number.isInteger(separatorLength) || separatorLength < 0) {
    throw new RangeError('Separator length must be a non-negative integer.');
  }
  const context = selectionContext(segments);
  const checked = checkedPoint(context, point);
  return context.segments
    .slice(0, checked.index)
    .reduce((total, segment) => total + segment.text.length + separatorLength, point.offset);
}

/** Converts a joined-transcript offset back to the nearest segment-local point. */
export function fromGlobalOffset(
  segments: readonly AnnotationTextSegment[],
  globalOffset: number,
  separatorLength = 1,
): SelectionPoint {
  if (!Number.isInteger(globalOffset) || globalOffset < 0) {
    throw new RangeError('Global offset must be a non-negative integer.');
  }
  if (!Number.isInteger(separatorLength) || separatorLength < 0) {
    throw new RangeError('Separator length must be a non-negative integer.');
  }
  const ordered = orderedSegments(segments);
  if (ordered.length === 0) throw new RangeError('At least one transcript segment is required.');
  let remaining = globalOffset;
  for (let index = 0; index < ordered.length; index += 1) {
    const segment = ordered[index]!;
    if (remaining <= segment.text.length) {
      return { segment_id: segment.id, offset: remaining };
    }
    remaining -= segment.text.length;
    if (index < ordered.length - 1) {
      if (remaining < separatorLength) {
        return { segment_id: segment.id, offset: segment.text.length };
      }
      remaining -= separatorLength;
    }
  }
  const last = ordered.at(-1)!;
  if (remaining > 0) throw new RangeError('Global offset is past the transcript end.');
  return { segment_id: last.id, offset: last.text.length };
}

/** Interpolates character anchors onto cue timing for persisted annotation bounds. */
export function selectionTimeBounds(
  segments: readonly AnnotationTextSegment[],
  selection: AnchoredSelection,
): { start_s: number; end_s: number } {
  const context = selectionContext(segments);
  const normalised = normaliseSelectionInContext(
    context,
    { segment_id: selection.start_segment_id, offset: selection.start_offset },
    { segment_id: selection.end_segment_id, offset: selection.end_offset },
  );
  if (!normalised) throw new RangeError('A timed selection cannot be empty.');
  const start = context.segments[context.indexById.get(normalised.start_segment_id)!]!;
  const end = context.segments[context.indexById.get(normalised.end_segment_id)!]!;
  if (
    start.start_s === undefined ||
    start.end_s === undefined ||
    end.start_s === undefined ||
    end.end_s === undefined
  ) {
    throw new RangeError('Timed selections require cue start and end times.');
  }
  const interpolate = (segment: AnnotationTextSegment, offset: number): number => {
    const duration = Math.max(0, segment.end_s! - segment.start_s!);
    const fraction = segment.text.length === 0 ? 0 : offset / segment.text.length;
    return segment.start_s! + duration * fraction;
  };
  return {
    start_s: interpolate(start, normalised.start_offset),
    end_s: interpolate(end, normalised.end_offset),
  };
}

/** Greedily assigns overlapping ranges to stable stacked indicator rails. */
export function layerOverlaps(
  ranges: readonly Omit<LayeredRange, 'layer'>[],
): LayeredRange[] {
  const layerEnds: number[] = [];
  return [...ranges]
    .filter((range) => range.end > range.start)
    .sort((left, right) => left.start - right.start || left.end - right.end || left.id.localeCompare(right.id))
    .map((range) => {
      let layer = layerEnds.findIndex((end) => range.start >= end);
      if (layer < 0) layer = layerEnds.length;
      layerEnds[layer] = range.end;
      return { ...range, layer };
    });
}

/** Returns local ranges for annotations that cover one transcript segment. */
export function annotationRangesForSegment(
  segments: readonly AnnotationTextSegment[],
  annotations: readonly AnchorAnnotation[],
  segmentId: string,
): AnnotationSegmentRange[] {
  return annotationRangesBySegment(segments, annotations).get(segmentId) ?? [];
}

/** Builds every segment-local annotation range with one segment sort and one pass per annotation. */
export function annotationRangesBySegment(
  segments: readonly AnnotationTextSegment[],
  annotations: readonly AnchorAnnotation[],
): Map<string, AnnotationSegmentRange[]> {
  const context = selectionContext(segments);
  const localBySegment = new Map<string, Array<Omit<AnnotationSegmentRange, 'layer'>>>();
  for (const annotation of annotations) {
    for (const slice of selectionSlicesInContext(context, annotation)) {
      const targetIndex = context.indexById.get(slice.segment_id)!;
      const target = context.segments[targetIndex]!;
      if (slice.start_offset >= target.text.length || slice.end_offset <= 0) continue;
      const local = localBySegment.get(slice.segment_id) ?? [];
      local.push({ ...annotation, start: slice.start_offset, end: slice.end_offset });
      localBySegment.set(slice.segment_id, local);
    }
  }
  const rangesBySegment = new Map<string, AnnotationSegmentRange[]>();
  for (const [segmentId, local] of localBySegment) {
    const layers = new Map(
      layerOverlaps(local.map(({ id, start, end }) => ({ id, start, end })))
        .map((range) => [range.id, range.layer]),
    );
    rangesBySegment.set(
      segmentId,
      local.map((range) => ({ ...range, layer: layers.get(range.id) ?? 0 })),
    );
  }
  return rangesBySegment;
}

export function filterAnnotations<T extends AnchorAnnotation>(
  annotations: readonly T[],
  segments: readonly AnnotationTextSegment[],
  filter: AnnotationFilter,
): T[] {
  const query = filter.query?.trim().toLocaleLowerCase('en-AU') ?? '';
  return annotations.filter((annotation) => {
    if (filter.reviewer_id && annotation.reviewer_id !== filter.reviewer_id) return false;
    if (filter.actions?.length && !filter.actions.includes(annotation.action)) return false;
    if (!query) return true;
    const haystack = `${selectionText(segments, annotation)} ${annotation.note ?? ''}`
      .toLocaleLowerCase('en-AU');
    return haystack.includes(query);
  });
}
